import React, { useEffect, useState } from 'react';
import { getRoleFromToken } from '../services/authService';
import { fetchRolePermissions } from '../services/apiService';

const Sidebar = ({ isOpen, sidebarRef, overlayRef, toggleSidebar }) => {
  const [rolePermissions, setRolePermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState({
    main: false,
    management: false,
    recruitment: false
  });
  
  const role = getRoleFromToken();

  useEffect(() => {
    const loadPermissions = async () => {
      try {
        const data = await fetchRolePermissions(role);
        const permissionsMap = data.reduce((acc, { role, permissionPath }) => {
          if (!acc[role]) acc[role] = [];
          acc[role].push(permissionPath);
          return acc;
        }, {});
        setRolePermissions(permissionsMap);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching role permissions:', error);
        setLoading(false);
      }
    };
    loadPermissions();
  }, [role]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const menuItems = {
    main: [
      { path: '/', label: 'Home', icon: 'fa-home', description: 'Dashboard overview' },
      { path: '/dashboard', label: 'Dashboard', icon: 'fa-chart-pie', description: 'Analytics & reports' },
      { path: '/calendar', label: 'Calendar', icon: 'fa-calendar', description: 'Schedule & events' }
    ],
    management: [
      { path: '/department', label: 'Departments', icon: 'fa-building', description: 'Manage departments' },
      { path: '/approval-levels', label: 'Approval Levels', icon: 'fa-level-up-alt', description: 'Configure approvals' },
      { path: '/users', label: 'Users', icon: 'fa-users', description: 'User management' },
      { path: '/roles', label: 'Roles', icon: 'fa-user-shield', description: 'Role permissions' },
      { path: '/permissions', label: 'Permissions', icon: 'fa-lock', description: 'Access control' },
      { path: '/audit-logs', label: 'Audit Logs', icon: 'fa-history', description: 'Activity tracking' }
    ],
    recruitment: [
      { path: '/demand-plan', label: 'Demand Plan', icon: 'fa-chart-line', description: 'Workforce planning' },
      { path: '/hiring-request', label: 'Hiring Request', icon: 'fa-file-alt', description: 'New requests' },
      { path: '/sourcing', label: 'Sourcing', icon: 'fa-search', description: 'Candidate sourcing' },
      { path: '/candidates', label: 'Candidates', icon: 'fa-user-tie', description: 'Candidate pool' },
      { path: '/candidates-pipeline', label: 'Pipeline', icon: 'fa-stream', description: 'Recruitment pipeline' },
      { path: '/interview-short-list', label: 'Interview Shortlist', icon: 'fa-list-check', description: 'Selected candidates' },
      { path: '/interview-board', label: 'Interview Board', icon: 'fa-people-arrows', description: 'Panel management' },
      { path: '/scheduled-interviews', label: 'Scheduled Interviews', icon: 'fa-clock', description: 'Upcoming interviews' },
      { path: '/short-list', label: 'Final Shortlist', icon: 'fa-star', description: 'Final selections' },
      { path: '/hiring-process', label: 'Hiring Process', icon: 'fa-diagram-project', description: 'Process tracking' },
      { path: '/hiring-approval', label: 'Hiring Approval', icon: 'fa-check-double', description: 'Pending approvals' },
      { path: '/offers', label: 'Offers', icon: 'fa-file-signature', description: 'Offer management' },
      { path: '/onboarding', label: 'Onboarding', icon: 'fa-user-plus', description: 'New hire onboarding' }
    ]
  };

  const renderMenuItem = (item) => {
    if (role && rolePermissions[role]?.includes(item.path)) {
      return (
        <li
          className="group relative overflow-hidden"
          key={item.path}
        >
          <a
            href={item.path}
            className="flex items-center p-3 rounded-lg bg-primary-800/5 hover:bg-primary-800 transition-all duration-300 group"
          >
            <div className="flex items-center space-x-3 flex-1">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-700/10 group-hover:bg-primary-600 transition-all duration-300">
                <i className={`fas ${item.icon} text-primary-700 group-hover:text-white text-sm`} />
              </div>
              <div className="flex-1">
                <span className="text-primary-800 group-hover:text-white font-medium block">
                  {item.label}
                </span>
                <span className="text-xs text-primary-600/70 group-hover:text-primary-100 block mt-0.5">
                  {item.description}
                </span>
              </div>
            </div>
            
            {/* Active indicator */}
            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-primary-600 rounded-r-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            
            {/* Hover effect gradient */}
            <div className="absolute inset-0 bg-gradient-to-r from-primary-600 to-primary-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10" />
          </a>
        </li>
      );
    }
    return null;
  };

  const renderSection = (title, sectionKey, items, icon) => {
    const hasVisibleItems = items.some(item => 
      role && rolePermissions[role]?.includes(item.path)
    );

    if (!hasVisibleItems) return null;

    return (
      <div className="mb-4">
        <button
          onClick={() => toggleSection(sectionKey)}
          className="w-full flex items-center justify-between p-3 rounded-lg bg-primary-800/10 hover:bg-primary-800/20 transition-all duration-300 mb-2"
        >
          <div className="flex items-center space-x-3">
            <i className={`fas ${icon} text-primary-700 text-sm`} />
            <span className="text-primary-800 font-semibold text-sm uppercase tracking-wider">
              {title}
            </span>
          </div>
          <i className={`fas fa-chevron-${expandedSections[sectionKey] ? 'up' : 'down'} text-primary-600 text-xs transition-transform duration-300`} />
        </button>
        
        {expandedSections[sectionKey] && (
          <ul className="space-y-1.5 pl-2">
            {items.map(item => renderMenuItem(item))}
          </ul>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="fixed top-0 left-0 h-full bg-white shadow-lg p-8 w-64 flex items-center justify-center">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-primary-600 text-3xl mb-3" />
          <p className="text-primary-800 text-sm">Loading menu...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Overlay for mobile */}
      <div
        ref={overlayRef}
        className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-10 transition-opacity duration-300 ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={toggleSidebar}
      />

      {/* Sidebar */}
      <div
        ref={sidebarRef}
        className={`fixed top-0 left-0 h-full bg-gradient-to-b from-white to-primary-50 shadow-2xl p-4 transform transition-transform duration-300 z-20
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          w-72 sm:w-80 md:w-80 lg:w-80 xl:w-80
          max-w-full overflow-y-auto scrollbar-thin scrollbar-thumb-primary-300 scrollbar-track-primary-100
        `}
      >
        {/* Header with user info */}
        <div className="mb-6 pb-4 border-b-2 border-primary-200">
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary-600 to-primary-700 flex items-center justify-center">
              <i className="fas fa-bolt text-white text-xl" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-primary-800">HR Portal</h2>
              <p className="text-xs text-primary-600">{role?.toUpperCase()} Access</p>
            </div>
          </div>
          
          {/* Quick stats or user info can go here */}
          <div className="flex items-center justify-between text-xs text-primary-600 mt-2">
            <span className="flex items-center">
              <i className="fas fa-circle text-green-500 text-[6px] mr-1" />
              Online
            </span>
            <button className="hover:text-primary-800 transition-colors">
              <i className="fas fa-cog" />
            </button>
          </div>
        </div>

        {/* Menu Sections */}
        <div className="space-y-2">
          {renderSection('QUICK ACCESS', 'main', menuItems.main, 'fa-rocket')}
          {renderSection('MANAGEMENT', 'management', menuItems.management, 'fa-cogs')}
          {renderSection('RECRUITMENT', 'recruitment', menuItems.recruitment, 'fa-users')}
        </div>

        {/* Footer with additional options */}
        <div className="mt-6 pt-4 border-t-2 border-primary-200">
          <button className="w-full flex items-center space-x-3 p-3 rounded-lg hover:bg-primary-800/10 transition-all duration-300 group">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-700/10 group-hover:bg-primary-600 transition-all duration-300">
              <i className="fas fa-question-circle text-primary-700 group-hover:text-white" />
            </div>
            <span className="text-primary-800 group-hover:text-primary-600 text-sm">Help & Support</span>
          </button>
          
          <button className="w-full flex items-center space-x-3 p-3 rounded-lg hover:bg-red-50 transition-all duration-300 group">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-100 group-hover:bg-red-500 transition-all duration-300">
              <i className="fas fa-sign-out-alt text-red-600 group-hover:text-white" />
            </div>
            <span className="text-red-600 group-hover:text-red-700 text-sm">Logout</span>
          </button>
        </div>

        {/* Version info */}
        <div className="mt-4 text-center">
          <p className="text-xs text-primary-400">Version 2.0.0</p>
        </div>
      </div>
    </>
  );
};

export default Sidebar;